import { useEffect, useState } from "react";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";

/**
 * Poll jetson_system_status on the ARC host for CPU%, temperature, hostname,
 * and device_type. Set REACT_APP_JETSON_SYSTEM_STATUS_URL to override the default
 * http://{arcHost}:12397/status (full URL including path).
 */
export function useJetsonSystemStatus(arcHost) {
    const [temperature, setTemperature] = useState(undefined);
    const [cpuPercent, setCpuPercent] = useState(undefined);
    const [hostname, setHostname] = useState(undefined);
    const [deviceType, setDeviceType] = useState(undefined);
    const [statusResolved, setStatusResolved] = useState(false);
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();

    useEffect(() => {
        if (!arcHost || String(arcHost).trim() === "") {
            return undefined;
        }

        setTemperature(undefined);
        setCpuPercent(undefined);
        setHostname(undefined);
        setDeviceType(undefined);
        setStatusResolved(false);

        const configured = process.env.REACT_APP_JETSON_SYSTEM_STATUS_URL?.trim();
        const gateway = gatewayServiceBaseUrl("system", arcId, arcHost);
        if (arcId && !configured && !gateway) return undefined;
        const rawUrl =
            configured ||
            (gateway
                ? `${gateway}/status`
                : `${isSecure ? "https" : "http"}://${arcHost}:12397/status`);
        const url = rawUrl;

        let cancelled = false;

        const tick = async () => {
            try {
                const res = await authedFetch(url, { cache: "no-store" });
                if (!res.ok) {
                    if (process.env.NODE_ENV === "development") {
                        // e.g. connection refused, 503 if jtop not ready
                        console.warn("[jetson_system_status] HTTP", res.status, url);
                    }
                    return;
                }
                const data = await res.json();
                if (cancelled) return;
                if (!data?.ok) {
                    if (process.env.NODE_ENV === "development") {
                        console.warn("[jetson_system_status] ok=false:", data?.error ?? data, url);
                    }
                    return;
                }
                setStatusResolved(true);
                if (typeof data.temperature_c === "number") {
                    setTemperature(data.temperature_c);
                }
                if (typeof data.cpu_percent === "number") {
                    setCpuPercent(data.cpu_percent);
                }
                if (typeof data.hostname === "string" && data.hostname.trim() !== "") {
                    setHostname(data.hostname.trim());
                }
                if (typeof data.device_type === "string" && data.device_type.trim() !== "") {
                    setDeviceType(data.device_type.trim());
                }
            } catch (e) {
                if (process.env.NODE_ENV === "development") {
                    // Network error, CORS, mixed content (HTTPS page → HTTP jetson), wrong host
                    console.warn("[jetson_system_status] fetch failed:", url, e);
                }
            }
        };

        tick();
        const id = setInterval(tick, 2000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [arcHost, isSecure, arcId]);

    return { temperature, cpuPercent, hostname, deviceType, statusResolved };
}
